/**
 * FAQs Page - Coordinator Component
 *
 * Refactored in Phase 3 to use decomposed components
 * Original: 816 lines → Refactored: ~200 lines (75% reduction)
 *
 * Architecture:
 * - This is now a lightweight coordinator that manages state and composes sub-components
 * - Each functional area extracted into focused, reusable components
 * - Performance optimized with useCallback for all handlers
 *
 * Components:
 * - FAQFilters: Search and category filtering
 * - FAQList: Display FAQ cards
 * - FAQStats: Statistics for moderators
 * - ModerationPanel: Pending FAQs approval workflow
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Badge,
  Alert,
} from '@mui/material';
import { PageLayout } from '../components/ui/PageLayout';
import {
  Add as AddIcon,
  Gavel as GavelIcon,
  ArrowForward as ViewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useSystem } from '../hooks/api/config';
import { FAQDocumentResponse, FAQDocumentCreate, ApprovalStatus, FAQSortBy } from '../api/data-contracts';
import { useFAQs, useRateFAQ, useUnrateFAQ, useCreateFAQ, useUpdateFAQ, useFAQStats, usePendingApprovalFAQs, useDeleteFAQ } from '../hooks/api/useFAQs';
import { FAQDialog } from '../components/faqs/FAQDialog';
import { FAQDetailDialog } from '../components/faqs/FAQDetailDialog';
import { DeleteConfirmationDialog } from '../components/ui/DeleteConfirmationDialog';
import { useSnackbar } from '../contexts/SnackbarContext';
import { log } from '../utils/errors/logger';
import { getDocumentVisibilityIcon, getDocumentVisibilityText } from '../utils/documents/documentHelpers';
import { usePermissions } from '../hooks/permissions/usePermissions';

// Import new decomposed components
import { FAQFilters, FAQList, FAQStats, ModerationPanel } from '../components/faqs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`faq-tabpanel-${index}`}
      aria-labelledby={`faq-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

/**
 * FAQs Component - Coordinator
 *
 * Manages state and orchestrates the FAQ sub-components
 * All helper functions wrapped in useCallback for performance
 */
const FAQs: React.FC = () => {
  const { t } = useTranslation();
  const { system } = useSystem();
  const { can, user } = usePermissions();
  const canModerate = can.manageUsers; // Moderators can manage FAQs
  const { showSuccess, showError } = useSnackbar();

  // URL search params for tab state persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab from URL or default to 0
  const tabValue = parseInt(searchParams.get('tab') || '0', 10);

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sortBy, setSortBy] = useState<FAQSortBy>('newest' as FAQSortBy);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQDocumentResponse | null>(null);
  const [selectedFAQIdForDetail, setSelectedFAQIdForDetail] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<FAQDocumentResponse | null>(null);

  // API Hooks
  const {
    data: faqResponse,
    isLoading,
    error,
    refetch
  } = useFAQs({
    category: selectedCategory || undefined,
    sortBy: searchQuery.trim() ? undefined : sortBy,
    limit: 50,
    skip: 0,
  });

  const { data: faqStats, isLoading: statsLoading } = useFAQStats();
  const { data: pendingFAQs, isLoading: pendingLoading, error: pendingError } = usePendingApprovalFAQs({
    page_size: 50,
  });

  // Mutations
  const rateFAQMutation = useRateFAQ();
  const unrateFAQMutation = useUnrateFAQ();
  const createFAQMutation = useCreateFAQ();
  const updateFAQMutation = useUpdateFAQ();
  const deleteFAQMutation = useDeleteFAQ({
    mutationOptions: {
      onSuccess: () => {
        showSuccess(t('faq.actions.delete_success'));
        setDeleteDialogOpen(false);
        setFaqToDelete(null);
      },
      onError: () => {
        showError(t('faq.actions.delete_error'));
      },
    },
  });

  // Log user info for debugging
  React.useEffect(() => {
    log.info('FAQ page loaded', {
      userRole: user?.role,
      canModerate,
      hasSystem: Boolean(system),
      tabValue,
      statsLoading
    });
  }, [user?.role, canModerate, system, tabValue, statsLoading]);

  /**
   * Filter and search FAQs locally for better UX
   * Memoized to prevent recalculation on every render
   */
  const filteredFaqs = useMemo(() => {
    if (!faqResponse?.faqs) {return [];}

    let filtered = faqResponse.faqs;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq =>
        faq.question?.toLowerCase().includes(query) ||
        faq.answer?.toLowerCase().includes(query) ||
        faq.category_tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(faq =>
        faq.category_tags?.includes(selectedCategory)
      );
    }

    return filtered;
  }, [faqResponse?.faqs, searchQuery, selectedCategory]);

  /**
   * Extract unique categories
   * Memoized to prevent recalculation
   */
  const categories = useMemo(() => {
    if (!faqResponse?.faqs) {return [];}

    const categorySet = new Set<string>();
    faqResponse.faqs.forEach(faq => {
      faq.category_tags?.forEach(tag => categorySet.add(tag));
    });

    return Array.from(categorySet).sort();
  }, [faqResponse?.faqs]);

  /**
   * Visibility helper functions - now imported from shared utils
   * Wrapped in useCallback to maintain stable references for child components
   */
  const getVisibilityIcon = useCallback(getDocumentVisibilityIcon, []);

  const getVisibilityText = useCallback((faq: FAQDocumentResponse) => {
    return getDocumentVisibilityText(
      {
        visibility: faq.visibility || undefined,
        shared_with_groups: faq.shared_with_groups
      },
      t
    );
  }, [t]);

  /**
   * Event handlers wrapped in useCallback for stable references
   */
  const handleRateFAQ = useCallback((faqId: string, helpful: boolean) => {
    rateFAQMutation.mutate({ faqId, helpful });
  }, [rateFAQMutation]);

  const handleUnrateFAQ = useCallback((faqId: string) => {
    unrateFAQMutation.mutate(faqId);
  }, [unrateFAQMutation]);

  const handleCreateFAQ = useCallback(() => {
    setEditingFAQ(null);
    setCreateDialogOpen(true);
  }, []);

  const handleEditFAQ = useCallback((faq: FAQDocumentResponse) => {
    setEditingFAQ(faq);
    setCreateDialogOpen(true);
  }, []);

  const handleViewDetails = useCallback((faq: FAQDocumentResponse) => {
    log.user.action('faq_view_details_clicked', 'FAQs', { faqId: faq.document_id });
    setSelectedFAQIdForDetail(faq.document_id);
  }, []);

  const handleDetailDialogClose = useCallback(() => {
    setSelectedFAQIdForDetail(null);
  }, []);

  const handleDialogClose = useCallback(() => {
    setCreateDialogOpen(false);
    setEditingFAQ(null);
  }, []);

  const handleFAQSubmit = useCallback(async (faqData: FAQDocumentCreate, isEdit: boolean) => {
    if (isEdit && editingFAQ) {
      await updateFAQMutation.mutateAsync({ faqId: editingFAQ.document_id, updateData: faqData });
    } else {
      await createFAQMutation.mutateAsync(faqData);
    }
  }, [editingFAQ, updateFAQMutation, createFAQMutation]);

  const handleTabChange = useCallback((newValue: number) => {
    log.user.navigation(`tab_${tabValue}`, `tab_${newValue}`);
    setSearchParams({ tab: newValue.toString() });
  }, [tabValue, setSearchParams]);

  const canApproveRejectFAQ = useCallback((faq: FAQDocumentResponse) => {
    const isOwner = Boolean(user && faq.owner_id === user.id);
    return {
      canApprove: canModerate,
      canReject: canModerate,
      canWithdraw: isOwner && faq.approval_status === ApprovalStatus.PendingReview
    };
  }, [can.manageUsers, canModerate]);

  const handleToggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const handleDeleteFAQ = useCallback((faq: FAQDocumentResponse) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (faqToDelete) {
      await deleteFAQMutation.mutateAsync(faqToDelete.document_id);
    }
  }, [faqToDelete, deleteFAQMutation]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setFaqToDelete(null);
  }, []);

  // Error state
  if (error) {
    return (
      <PageLayout
        title={t('faq.titleLong')}
        isLoading={false}
        maxWidth="lg"
      >
        <Alert severity="error">
          {t('faq.loading')} - {error.message}
        </Alert>
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout
        title={t('faq.titleLong')}
        isLoading={false}
        maxWidth="lg"
        headerActions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateFAQ}
          >
            {t('faq.createButton')}
          </Button>
        }
        headerContent={
          <>
            {/* FAQ Stats Banner for Moderators */}
            {canModerate && faqStats && faqStats.pending_review > 0 && (
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: 'warning.lighter',
                  border: '1px solid',
                  borderColor: 'warning.main',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Badge badgeContent={faqStats.pending_review} color="warning">
                      <GavelIcon color="warning" />
                    </Badge>
                    <Typography variant="body1" color="warning.dark">
                      {t('faq.moderation.pendingCount', { count: faqStats.pending_review })}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={() => handleTabChange(1)}
                    endIcon={<ViewIcon />}
                  >
                    {t('faq.moderation.goToModeration')}
                  </Button>
                </Box>
              </Paper>
            )}
          </>
        }
      >

        {/* Tabs */}
        <Paper sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', p: 2 }}>
            <Button
              variant={tabValue === 0 ? 'contained' : 'text'}
              onClick={() => handleTabChange(0)}
              sx={{ mr: 1, textTransform: 'none' }}
            >
              {t('faq.tabs.all')}
            </Button>
            {canModerate && (
              <Button
                variant={tabValue === 1 ? 'contained' : 'text'}
                onClick={() => handleTabChange(1)}
                sx={{ mr: 1, textTransform: 'none' }}
              >
                {t('faq.tabs.moderation')}
              </Button>
            )}
          </Box>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* All FAQs Tab */}
          <FAQFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            categories={categories}
            sortBy={sortBy}
            onSortChange={(value) => setSortBy(value as FAQSortBy)}
            filteredCount={filteredFaqs.length}
            totalCount={faqResponse?.faqs?.length || 0}
            showFilters={showFilters}
            onToggleFilters={handleToggleFilters}
          />

          <FAQList
            faqs={filteredFaqs}
            isLoading={isLoading}
            isModerator={canModerate}
            user={user}
            onRateFAQ={handleRateFAQ}
            onUnrateFAQ={handleUnrateFAQ}
            onViewDetails={handleViewDetails}
            onEditFAQ={handleEditFAQ}
            onDeleteFAQ={handleDeleteFAQ}
            getVisibilityIcon={getVisibilityIcon}
            getVisibilityText={getVisibilityText}
            showSuccess={showSuccess}
            showError={showError}
          />
        </TabPanel>

        {canModerate && (
          <TabPanel value={tabValue} index={1}>
            {/* Moderation Tab */}
            <FAQStats
              stats={faqStats}
              isLoading={statsLoading}
            />

            <ModerationPanel
              pendingFAQs={pendingFAQs?.faqs || []}
              isLoading={pendingLoading}
              error={pendingError}
              canApproveRejectFAQ={canApproveRejectFAQ}
              getVisibilityIcon={getVisibilityIcon}
              getVisibilityText={getVisibilityText}
              showSuccess={showSuccess}
              showError={showError}
            />
          </TabPanel>
        )}

      </PageLayout>

      {/* FAQ Dialog */}
      <FAQDialog
        open={createDialogOpen}
        onClose={handleDialogClose}
        editingFAQ={editingFAQ}
        onSubmit={handleFAQSubmit}
      />

      {/* FAQ Detail Dialog */}
      <FAQDetailDialog
        open={Boolean(selectedFAQIdForDetail)}
        onClose={handleDetailDialogClose}
        faqId={selectedFAQIdForDetail}
        onEdit={handleEditFAQ}
        onDelete={handleDeleteFAQ}
        isModerator={canModerate}
        currentUserId={user?.id || null}
        onRateFAQ={handleRateFAQ}
        onUnrateFAQ={handleUnrateFAQ}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('faq.actions.delete_confirm_title')}
        message={t('faq.actions.delete_confirm_message')}
        loading={deleteFAQMutation.isPending}
      />
    </>
  );
};

export default FAQs;
